import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/apiService';
import toast from 'react-hot-toast';
import { X } from 'lucide-react';
import './SupportRequestModal.css';

export interface SupportRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const SUPPORT_EMAIL = 'support@cognixisai.com';

export const SupportRequestModal: React.FC<SupportRequestModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { profile } = useAuth();
  const [type, setType] = useState<'service_request' | 'suggestion' | 'bug_report'>('bug_report');
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Pre-populate user info if logged in
  useEffect(() => {
    if (profile) {
      setEmail(profile.email || '');
      setName(`${profile.firstName || ''} ${profile.lastName || ''}`.trim() || '');
    } else {
      setEmail('');
      setName('');
    }
  }, [profile, isOpen]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setType('bug_report');
      setSubject('');
      setDescription('');
      if (!profile) {
        setEmail('');
        setName('');
      }
    }
  }, [isOpen, profile]);

  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  const validateForm = (): boolean => {
    if (!subject.trim()) {
      toast.error('Please enter a subject', {
        duration: 3000,
        position: 'top-right',
      });
      return false;
    }
    if (!description.trim()) {
      toast.error('Please enter a description', {
        duration: 3000,
        position: 'top-right',
      });
      return false;
    }
    if (!email.trim()) {
      toast.error('Please enter your email address', {
        duration: 3000,
        position: 'top-right',
      });
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error('Please enter a valid email address', {
        duration: 3000,
        position: 'top-right',
      });
      return false;
    }
    if (!name.trim()) {
      toast.error('Please enter your name', {
        duration: 3000,
        position: 'top-right',
      });
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      await apiService.submitSupportRequest({
        type,
        subject: subject.trim(),
        description: description.trim(),
        email: email.trim(),
        name: name.trim(),
        profileId: profile?.id,
      });

      toast.success('Support request submitted successfully!', {
        duration: 3000,
        position: 'top-right',
        style: {
          background: '#10b981',
          color: '#fff',
          fontWeight: '500',
        },
      });

      // Reset form and close modal
      setSubject('');
      setDescription('');
      if (!profile) {
        setEmail('');
        setName('');
      }
      onClose();
    } catch (error: any) {
      toast.error(error?.message || 'Failed to submit support request. Please try again.', {
        duration: 5000,
        position: 'top-right',
        style: {
          background: '#dc2626',
          color: '#fff',
          fontWeight: '500',
        },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEmailClientFallback = () => {
    const typeDisplay = type.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase());
    const emailSubject = encodeURIComponent(`[Support Request] ${typeDisplay}: ${subject || 'No Subject'}`);
    
    const emailBody = encodeURIComponent(`Request Type: ${typeDisplay}
Name: ${name || 'Not provided'}
Email: ${email || 'Not provided'}
${profile ? `User ID: ${profile.id}` : 'User ID: Not logged in'}

Description:
${description || 'No description provided'}

---
Please provide any additional information that may help us assist you better.
`);

    const mailtoLink = `mailto:${SUPPORT_EMAIL}?subject=${emailSubject}&body=${emailBody}`;
    window.open(mailtoLink, '_blank');
  };

  return (
    <div
      className="support-request-modal-overlay"
      onClick={handleBackdropClick}
      onKeyDown={handleKeyDown}
      role="dialog"
      aria-modal="true"
      aria-labelledby="support-request-modal-title"
      tabIndex={-1}
    >
      <div className="support-request-modal-content">
        <button
          className="support-request-modal-close"
          onClick={onClose}
          aria-label="Close support request modal"
        >
          <X size={20} />
        </button>

        <div className="support-request-modal-body">
          <h2 id="support-request-modal-title" className="support-request-modal-title">
            Contact Support
          </h2>
          <p className="support-request-modal-subtitle">
            Submit a service request, suggestion, or report a bug. We'll get back to you as soon as possible.
          </p>

          <form onSubmit={handleSubmit} className="support-request-form">
            <div className="support-request-field">
              <label htmlFor="type" className="support-request-label">
                Request Type <span className="required">*</span>
              </label>
              <select
                id="type"
                value={type}
                onChange={(e) => setType(e.target.value as typeof type)}
                className="support-request-select"
                required
              >
                <option value="bug_report">Bug Report</option>
                <option value="service_request">Service Request</option>
                <option value="suggestion">Suggestion</option>
              </select>
            </div>

            <div className="support-request-field">
              <label htmlFor="subject" className="support-request-label">
                Subject <span className="required">*</span>
              </label>
              <input
                id="subject"
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="support-request-input"
                placeholder="Brief description of your request"
                required
              />
            </div>

            <div className="support-request-field">
              <label htmlFor="description" className="support-request-label">
                Description <span className="required">*</span>
              </label>
              <textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="support-request-textarea"
                placeholder="Please provide detailed information about your request, bug, or suggestion..."
                rows={6}
                required
              />
            </div>

            <div className="support-request-field">
              <label htmlFor="name" className="support-request-label">
                Your Name <span className="required">*</span>
              </label>
              <input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="support-request-input"
                placeholder="Your full name"
                required
              />
            </div>

            <div className="support-request-field">
              <label htmlFor="email" className="support-request-label">
                Your Email <span className="required">*</span>
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="support-request-input"
                placeholder="your.email@example.com"
                required
              />
            </div>

            <div className="support-request-actions">
              <button
                type="button"
                onClick={handleEmailClientFallback}
                className="support-request-button-secondary"
                disabled={isSubmitting}
              >
                Open Email Client
              </button>
              <button
                type="submit"
                className="support-request-button-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Request'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
